import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMaterialShapes, getMaterialTypes } from '../api';
import { BUILT_IN_MATERIAL_TYPES, effectiveMaterialTypes, resolveMaterialType as resolveType } from '../materials/materialTypes';
import { BUILT_IN_MATERIAL_SHAPES, effectiveMaterialShapes, resolveMaterialShape as resolveShape } from '../materials/materialShapes';

export const MaterialTypesContext = createContext({
  materialTypes: BUILT_IN_MATERIAL_TYPES,
  activeMaterialTypes: BUILT_IN_MATERIAL_TYPES,
  resolveMaterialType: (value, legacyLabel) => resolveType(value, BUILT_IN_MATERIAL_TYPES, legacyLabel),
  materialShapes: BUILT_IN_MATERIAL_SHAPES,
  activeMaterialShapes: BUILT_IN_MATERIAL_SHAPES,
  resolveMaterialShape: (value, legacyLabel) => resolveShape(value, BUILT_IN_MATERIAL_SHAPES, legacyLabel),
  reloadMaterialTypes: async () => {},
  reloadMaterialShapes: async () => {},
  materialTypesLoading: false,
  materialTypesError: '',
  materialTypesAvailable: true,
  materialShapesLoading: false,
  materialShapesError: '',
  materialShapesAvailable: true,
  materialCatalogLoading: false,
  materialCatalogError: '',
  materialCatalogAvailable: true,
});

export const MaterialTypesProvider = ({ children, companyID }) => {
  const [materialTypes, setMaterialTypes] = useState([]);
  const [materialShapes, setMaterialShapes] = useState([]);
  const [materialTypesLoading, setMaterialTypesLoading] = useState(true);
  const [materialTypesError, setMaterialTypesError] = useState('');
  const [materialTypesAvailable, setMaterialTypesAvailable] = useState(false);
  const [materialShapesLoading, setMaterialShapesLoading] = useState(true);
  const [materialShapesError, setMaterialShapesError] = useState('');
  const [materialShapesAvailable, setMaterialShapesAvailable] = useState(false);
  const requestSequence = useRef(0);

  const reloadMaterialDefinitions = useCallback(async () => {
    const requestID = ++requestSequence.current;
    setMaterialTypesLoading(true);
    setMaterialShapesLoading(true);
    setMaterialTypesError('');
    setMaterialShapesError('');
    const [typesResult, shapesResult] = await Promise.allSettled([getMaterialTypes(), getMaterialShapes()]);
    if (requestID !== requestSequence.current) return;
    if (typesResult.status === 'fulfilled') {
      setMaterialTypes(effectiveMaterialTypes(Array.isArray(typesResult.value) ? typesResult.value : []));
      setMaterialTypesAvailable(true);
    } else {
      setMaterialTypes([]);
      setMaterialTypesAvailable(false);
      setMaterialTypesError(typesResult.reason?.message || '소재 종류 카탈로그 요청 실패');
    }
    if (shapesResult.status === 'fulfilled') {
      setMaterialShapes(effectiveMaterialShapes(Array.isArray(shapesResult.value) ? shapesResult.value : []));
      setMaterialShapesAvailable(true);
    } else {
      setMaterialShapes([]);
      setMaterialShapesAvailable(false);
      setMaterialShapesError(shapesResult.reason?.message || '소재 모양 카탈로그 요청 실패');
    }
    setMaterialTypesLoading(false);
    setMaterialShapesLoading(false);
  }, []);

  useEffect(() => {
    setMaterialTypes([]);
    setMaterialShapes([]);
    setMaterialTypesAvailable(false);
    setMaterialShapesAvailable(false);
    reloadMaterialDefinitions();
    return () => { requestSequence.current += 1; };
  }, [companyID, reloadMaterialDefinitions]);

  const value = useMemo(() => ({
    materialTypes,
    activeMaterialTypes: materialTypes.filter((material) => material.active),
    resolveMaterialType: (materialValue, legacyLabel) => resolveType(materialValue, materialTypes, legacyLabel, materialTypesAvailable),
    materialShapes,
    activeMaterialShapes: materialShapes.filter((shape) => shape.active),
    resolveMaterialShape: (shapeValue, legacyLabel) => resolveShape(shapeValue, materialShapes, legacyLabel, materialShapesAvailable),
    reloadMaterialTypes: reloadMaterialDefinitions,
    reloadMaterialShapes: reloadMaterialDefinitions,
    materialTypesLoading,
    materialTypesError,
    materialTypesAvailable,
    materialShapesLoading,
    materialShapesError,
    materialShapesAvailable,
    materialCatalogLoading: materialTypesLoading || materialShapesLoading,
    materialCatalogError: materialTypesError || materialShapesError,
    materialCatalogAvailable: !materialTypesLoading && !materialShapesLoading && materialTypesAvailable && materialShapesAvailable,
  }), [
    materialTypes, materialShapes, reloadMaterialDefinitions,
    materialTypesLoading, materialTypesError, materialTypesAvailable,
    materialShapesLoading, materialShapesError, materialShapesAvailable,
  ]);

  return <MaterialTypesContext.Provider value={value}>{children}</MaterialTypesContext.Provider>;
};
